import { useEffect, useState } from "react";
import { Loader2, Wrench, TruckIcon, CheckCircle2, Plus, Pencil, Trash2, Droplets, Package } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { PemakaianSparepart } from "../components/PemakaianSparepart";
import { BarangCombobox } from "../components/BarangCombobox";
import api, { formatApiError } from "../api";

const KATEGORI_SERVIS = ["Ganti Oli", "Filter", "Rem", "Mesin", "Ban", "Per", "Lainnya"];
const fmtKM = (v) => `${Number(v || 0).toLocaleString("id-ID")} KM`;
const fmtRp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const fmtDate = (s) => {
  const d = new Date(`${s}T00:00:00`);
  return isNaN(d) ? s : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const emptyForm = { Plat_Truk: "", Tanggal_Masuk_Bengkel: "", Odometer_Saat_Servis_KM: "", Kategori_Servis: "", Total_Biaya_Servis: "" };

const ServisDialog = ({ open, onClose, editing, kendaraan, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [gudang, setGudang] = useState([]);
  const [parts, setParts] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        Plat_Truk: editing.Plat_Truk,
        Tanggal_Masuk_Bengkel: editing.Tanggal_Masuk_Bengkel,
        Odometer_Saat_Servis_KM: String(editing.Odometer_Saat_Servis_KM),
        Kategori_Servis: editing.Kategori_Servis,
        Total_Biaya_Servis: String(editing.Total_Biaya_Servis),
      } : emptyForm);
      setParts([]);
      if (!editing) api.get("/gudang").then(({ data }) => setGudang(data)).catch(() => {});
    }
  }, [open, editing]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setPart = (i, k, v) => setParts((p) => p.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const partsSubtotal = parts.reduce((sum, p) => {
    const b = gudang.find((g) => g.id === p.barang_id);
    return sum + (b ? (parseInt(p.jumlah, 10) || 0) * b.Harga_Beli_Satuan : 0);
  }, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.Plat_Truk || !form.Tanggal_Masuk_Bengkel || !form.Kategori_Servis || form.Odometer_Saat_Servis_KM === "" || form.Total_Biaya_Servis === "") {
      toast.error("Semua kolom wajib diisi");
      return;
    }
    const validParts = parts.filter((p) => p.barang_id && parseInt(p.jumlah, 10) > 0);
    if (parts.length > 0 && validParts.length < parts.length) {
      toast.error("Lengkapi barang & jumlah pada setiap baris sparepart, atau hapus baris kosong");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        Odometer_Saat_Servis_KM: parseInt(form.Odometer_Saat_Servis_KM, 10),
        Total_Biaya_Servis: parseFloat(form.Total_Biaya_Servis),
      };
      if (editing) {
        await api.put(`/servis/${editing.id}`, payload);
        toast.success("Riwayat servis diperbarui");
      } else {
        const { data: created } = await api.post("/servis", payload);
        let dicatat = 0;
        for (const p of validParts) {
          try {
            await api.post(`/servis/${created.id}/onderdil`, { barang_id: p.barang_id, Jumlah_Dipakai: parseInt(p.jumlah, 10) });
            dicatat += 1;
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
          }
        }
        toast.success(dicatat > 0
          ? `Servis dicatat + ${dicatat} sparepart dipakai (stok gudang otomatis berkurang)`
          : "Riwayat servis dicatat");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="servis-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit Servis ${editing.ID_Servis}` : "Catat Servis Baru"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Plat Truk</Label>
            <Select value={form.Plat_Truk} onValueChange={set("Plat_Truk")}>
              <SelectTrigger data-testid="servis-plat-select"><SelectValue placeholder="Pilih truk" /></SelectTrigger>
              <SelectContent>
                {kendaraan.map((k) => <SelectItem key={k.id} value={k.Plat_Nomor}>{k.Plat_Nomor} — {k.Jenis_Truk}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tanggal Masuk Bengkel</Label>
              <Input data-testid="servis-tanggal-input" type="date" value={form.Tanggal_Masuk_Bengkel}
                onChange={(e) => set("Tanggal_Masuk_Bengkel")(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Odometer Saat Servis (KM)</Label>
              <Input data-testid="servis-odometer-input" type="number" min="0" step="1" placeholder="cth: 125000"
                value={form.Odometer_Saat_Servis_KM} onChange={(e) => set("Odometer_Saat_Servis_KM")(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Kategori Servis</Label>
            <Select value={form.Kategori_Servis} onValueChange={set("Kategori_Servis")}>
              <SelectTrigger data-testid="servis-kategori-select"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
              <SelectContent>
                {KATEGORI_SERVIS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Total Biaya Servis (Rp)</Label>
            <Input data-testid="servis-biaya-input" type="number" min="0" step="1" placeholder="cth: 850000"
              value={form.Total_Biaya_Servis} onChange={(e) => set("Total_Biaya_Servis")(e.target.value)} />
            {!editing && <p className="text-xs text-zinc-500">Isi biaya jasa/bengkel saja — nilai sparepart di bawah otomatis ditambahkan.</p>}
          </div>
          {!editing && (
            <div className="space-y-2 bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid="servis-parts-section">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Sparepart Dipakai (dari Inventaris Gudang)</Label>
                <Button data-testid="servis-add-part-button" type="button" size="sm" variant="outline"
                  className="h-7 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                  onClick={() => setParts((p) => [...p, { barang_id: "", jumlah: "1" }])}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Tambah
                </Button>
              </div>
              {parts.length === 0 ? (
                <p className="text-xs text-zinc-400">Opsional — stok gudang otomatis berkurang saat servis disimpan.</p>
              ) : (
                <div className="space-y-2">
                  {parts.map((p, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`servis-part-row-${i}`}>
                      <div className="flex-1 min-w-0">
                        <BarangCombobox gudang={gudang} value={p.barang_id}
                          onChange={(v) => setPart(i, "barang_id", v)} testId={`servis-part-barang-${i}`} />
                      </div>
                      <Input data-testid={`servis-part-jumlah-${i}`} type="number" min="1" step="1" value={p.jumlah}
                        onChange={(e) => setPart(i, "jumlah", e.target.value)} className="w-20 bg-white" />
                      <Button data-testid={`servis-part-remove-${i}`} type="button" size="icon" variant="ghost"
                        className="h-8 w-8 text-zinc-400 hover:text-red-600 shrink-0"
                        onClick={() => setParts((rows) => rows.filter((_, idx) => idx !== i))}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <p data-testid="servis-parts-subtotal" className="text-xs text-right font-medium text-emerald-800">
                    Nilai sparepart: {fmtRp(partsSubtotal)}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="servis-cancel-button" type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button data-testid="servis-submit-button" type="submit" disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Simpan Perubahan" : "Simpan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OnderdilDialog = ({ servis, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [gudang, setGudang] = useState([]);
  const [barangId, setBarangId] = useState("");
  const [jumlah, setJumlah] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/servis/${servis.id}/onderdil`).then(({ data }) => setData(data)).catch(() => setData({ items: [], Total_Biaya_Servis: servis.Total_Biaya_Servis }));
    api.get("/gudang").then(({ data }) => setGudang(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!barangId || !jumlah) {
      toast.error("Pilih barang dan isi jumlah");
      return;
    }
    setSaving(true);
    try {
      const { data: res } = await api.post(`/servis/${servis.id}/onderdil`, { barang_id: barangId, Jumlah_Dipakai: parseInt(jumlah, 10) });
      toast.success(`${res.Nama_Barang} ×${res.Jumlah_Dipakai} dicatat — stok tersisa ${res.Stok_Tersisa}, biaya servis jadi ${fmtRp(res.Total_Biaya_Servis)}`);
      setBarangId("");
      setJumlah("");
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Hapus pemakaian ${item.Nama_Barang} ×${item.Jumlah_Dipakai}? Stok akan dikembalikan.`)) return;
    try {
      await api.delete(`/servis/${servis.id}/onderdil/${item.id}`);
      toast.success("Pemakaian dihapus, stok dikembalikan");
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="onderdil-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pemakaian Onderdil — {servis.ID_Servis} ({servis.Plat_Truk})</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-3 items-end bg-zinc-50 border border-zinc-200 rounded-md p-4">
          <div className="space-y-1.5">
            <Label>Barang (dari Inventaris Gudang)</Label>
            <BarangCombobox gudang={gudang} value={barangId} onChange={setBarangId} testId="onderdil-barang-select" />
          </div>
          <div className="space-y-1.5">
            <Label>Jumlah Dipakai</Label>
            <Input data-testid="onderdil-jumlah-input" type="number" min="1" step="1" value={jumlah}
              onChange={(e) => setJumlah(e.target.value)} />
          </div>
          <Button data-testid="onderdil-submit-button" type="submit" disabled={saving}
            className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Catat
          </Button>
        </form>
        {data === null ? (
          <div className="flex items-center justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : data.items.length === 0 ? (
          <p data-testid="onderdil-empty" className="py-6 text-center text-sm text-zinc-400">Belum ada onderdil dipakai pada servis ini</p>
        ) : (
          <table className="w-full text-sm" data-testid="onderdil-table">
            <thead>
              <tr className="border-y border-zinc-200 bg-zinc-50">
                {["Kode", "Nama Barang", "Harga Satuan", "Jumlah", "Subtotal", ""].map((h, i) => (
                  <th key={i} className="text-left px-3 py-2 text-xs uppercase tracking-wider text-zinc-500 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} data-testid={`onderdil-row-${it.id}`} className="border-b border-zinc-100">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{it.Kode_Barang}</td>
                  <td className="px-3 py-2">{it.Nama_Barang}</td>
                  <td className="px-3 py-2 tabular-nums whitespace-nowrap">{fmtRp(it.Harga_Satuan)}</td>
                  <td className="px-3 py-2 tabular-nums">{it.Jumlah_Dipakai}</td>
                  <td className="px-3 py-2 tabular-nums whitespace-nowrap font-medium">{fmtRp(it.Subtotal)}</td>
                  <td className="px-3 py-2">
                    <Button data-testid={`onderdil-delete-${it.id}`} size="icon" variant="ghost" className="h-7 w-7 text-zinc-400 hover:text-red-600"
                      onClick={() => remove(it)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && (
          <p data-testid="onderdil-total-biaya" className="text-right text-sm font-semibold text-emerald-800">
            Total Biaya Servis (termasuk onderdil): {fmtRp(data.Total_Biaya_Servis)}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function ArmadaPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "owner";
  const [servis, setServis] = useState(null);
  const [logs, setLogs] = useState(null);
  const [riwayat, setRiwayat] = useState(null);
  const [kendaraan, setKendaraan] = useState([]);
  const [oliAlerts, setOliAlerts] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [onderdilServis, setOnderdilServis] = useState(null);

  const load = () => {
    api.get("/armada/servis").then(({ data }) => setServis(data)).catch(() => setServis([]));
    api.get("/armada/log").then(({ data }) => setLogs(data)).catch(() => setLogs([]));
    api.get("/servis").then(({ data }) => setRiwayat(data)).catch(() => setRiwayat([]));
    api.get("/servis/alerts").then(({ data }) => setOliAlerts(data)).catch(() => {});
    api.get("/kendaraan").then(({ data }) => setKendaraan(data)).catch(() => {});
  };
  useEffect(load, []);

  const markServiced = async (item) => {
    try {
      await api.post(`/armada/servis/${item.id}/reset`);
      toast.success(`${item.Plat_Truk} ditandai sudah servis, akumulasi KM di-reset`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeServis = async (item) => {
    if (!window.confirm(`Hapus riwayat servis ${item.ID_Servis} (${item.Plat_Truk})?`)) return;
    try {
      await api.delete(`/servis/${item.id}`);
      toast.success("Riwayat servis dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="armada-page" className="space-y-6 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Manajemen Armada &amp; Servis</h1>
          <p className="text-sm text-zinc-500 mt-1">Riwayat servis truk, akumulasi kilometer, dan pengingat ganti oli tiap 5.000 KM</p>
        </div>
        {isAdmin && (
          <Button data-testid="add-servis-button" onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 shrink-0">
            <Plus className="w-4 h-4 mr-2" /> Catat Servis
          </Button>
        )}
      </div>

      {oliAlerts && oliAlerts.count > 0 && (
        <div data-testid="armada-oli-alert" className="bg-yellow-50 border border-yellow-400 rounded-lg p-4 flex items-start gap-3">
          <Droplets className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-yellow-900">WAKTUNYA GANTI OLI — {oliAlerts.count} truk melewati {Number(oliAlerts.interval_km).toLocaleString("id-ID")} KM sejak servis terakhir</p>
            <ul className="mt-1 space-y-0.5">
              {oliAlerts.items.map((a) => (
                <li key={a.Plat_Truk} className="text-sm text-yellow-800">
                  <span className="font-semibold">{a.Plat_Truk}</span> — odometer {fmtKM(a.Odometer_Terkini_KM)}, servis terakhir di {fmtKM(a.Odometer_Servis_Terakhir_KM)} (+{fmtKM(a.Selisih_KM)})
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Wrench className="w-4 h-4 text-emerald-700" /> Riwayat Servis Truk
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {riwayat === null ? (
            <div className="flex items-center justify-center py-12 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : riwayat.length === 0 ? (
            <p data-testid="riwayat-servis-empty" className="px-5 py-10 text-center text-sm text-zinc-400">Belum ada riwayat servis. Klik "Catat Servis" untuk menambahkan.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="riwayat-servis-table">
                <thead>
                  <tr className="border-y border-zinc-200 bg-zinc-50">
                    {["ID Servis", "Tgl Masuk Bengkel", "Plat Truk", "Odometer Saat Servis", "Kategori", "Total Biaya", isAdmin ? "Aksi" : null].filter(Boolean).map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {riwayat.map((r) => (
                    <tr key={r.id} data-testid={`riwayat-row-${r.ID_Servis}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                      <td className="px-4 py-2.5 font-medium text-zinc-900 whitespace-nowrap">{r.ID_Servis}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(r.Tanggal_Masuk_Bengkel)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{r.Plat_Truk}</td>
                      <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">{fmtKM(r.Odometer_Saat_Servis_KM)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <Badge className="bg-zinc-100 text-zinc-800 border border-zinc-200 font-medium">{r.Kategori_Servis}</Badge>
                      </td>
                      <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">{fmtRp(r.Total_Biaya_Servis)}</td>
                      {isAdmin && (
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Button data-testid={`onderdil-servis-${r.ID_Servis}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-emerald-700"
                              onClick={() => setOnderdilServis(r)} title="Pemakaian Onderdil">
                              <Package className="w-3.5 h-3.5" />
                            </Button>
                            <Button data-testid={`edit-servis-${r.ID_Servis}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-emerald-700"
                              onClick={() => { setEditing(r); setDialogOpen(true); }}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button data-testid={`delete-servis-${r.ID_Servis}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-red-600"
                              onClick={() => removeServis(r)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <PemakaianSparepart kendaraan={kendaraan} isAdmin={isAdmin} />

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Wrench className="w-4 h-4 text-emerald-700" /> Status Servis per Truk (Akumulasi Trip)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {servis === null ? (
            <div className="flex items-center justify-center py-12 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : servis.length === 0 ? (
            <p data-testid="servis-empty" className="px-5 py-10 text-center text-sm text-zinc-400">Belum ada data. Akumulasi KM terisi otomatis saat trip berstatus "Selesai".</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="servis-table">
                <thead>
                  <tr className="border-y border-zinc-200 bg-zinc-50">
                    {["Plat Truk", "Akumulasi KM", "Interval Servis", "Progres", "Status", "Aksi"].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {servis.map((s) => {
                    const pct = Math.min(100, Math.round((s.Akumulasi_KM / s.Servis_Interval_KM) * 100));
                    return (
                      <tr key={s.id} data-testid={`servis-row-${s.Plat_Truk}`} className={`border-b border-zinc-100 transition-colors duration-200 ${s.servis_due ? "bg-red-50 hover:bg-red-100" : "hover:bg-zinc-50"}`}>
                        <td className="px-4 py-3 font-medium text-zinc-900 whitespace-nowrap">{s.Plat_Truk}</td>
                        <td className="px-4 py-3 tabular-nums whitespace-nowrap">{fmtKM(s.Akumulasi_KM)}</td>
                        <td className="px-4 py-3 tabular-nums whitespace-nowrap">{fmtKM(s.Servis_Interval_KM)}</td>
                        <td className="px-4 py-3 min-w-[140px]">
                          <div className="h-2 rounded-full bg-zinc-200 overflow-hidden">
                            <div className={`h-full rounded-full ${s.servis_due ? "bg-red-500" : "bg-emerald-600"}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-zinc-500">{pct}%</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge data-testid={`servis-status-${s.Plat_Truk}`} className={s.servis_due ? "bg-red-100 text-red-800 border border-red-200 font-medium" : "bg-emerald-100 text-emerald-800 border border-emerald-200 font-medium"}>
                            {s.servis_due ? "Perlu Servis" : "OK"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {isAdmin && (
                            <Button data-testid={`reset-servis-${s.Plat_Truk}`} size="sm" variant="outline" onClick={() => markServiced(s)}
                              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200">
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Sudah Servis
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            <TruckIcon className="w-4 h-4 text-emerald-700" /> Riwayat Kilometer per Trip
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs === null ? (
            <div className="flex items-center justify-center py-12 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : logs.length === 0 ? (
            <p data-testid="log-empty" className="px-5 py-10 text-center text-sm text-zinc-400">Belum ada riwayat</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="armada-log-table">
                <thead>
                  <tr className="border-y border-zinc-200 bg-zinc-50">
                    {["Waktu", "ID Trip", "Plat Truk", "Jarak Tempuh"].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} data-testid={`log-row-${l.ID_Trip}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                      <td className="px-4 py-2.5 whitespace-nowrap">{fmtTime(l.created_at)}</td>
                      <td className="px-4 py-2.5 font-medium whitespace-nowrap">{l.ID_Trip}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{l.Plat_Truk}</td>
                      <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">{fmtKM(l.Jarak_KM)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ServisDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} kendaraan={kendaraan} onSaved={load} />
      {onderdilServis && <OnderdilDialog servis={onderdilServis} onClose={() => setOnderdilServis(null)} onChanged={load} />}
    </div>
  );
}
